import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../../services/apiClient";
import "./UpdateLog.css";

const PAGE_SIZE = 12;

function prettyDate(date, options = {}) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ""))) return "";
  return new Date(date + "T12:00:00Z").toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
    ...options,
  });
}

function monthLabel(date) {
  return prettyDate(date, { day: undefined });
}

function titleFor(entry) {
  return entry.version ? entry.version + " — " + entry.title : entry.title;
}

function UpdateCard({ entry, expanded, onToggle }) {
  const hasMore = entry.highlights.length > 2 || Boolean(entry.whyItMatters);
  const displayedHighlights = expanded ? entry.highlights : entry.highlights.slice(0, 2);
  const anchor = "update-" + entry.id;

  return (
    <article className="ec-updates-card" id={anchor}>
      <div className="ec-updates-card-top">
        <span className="ec-updates-category">{entry.category}</span>
        <time dateTime={entry.date}>{prettyDate(entry.date)}</time>
      </div>
      <div className="ec-updates-card-heading">
        <h3>{titleFor(entry)}</h3>
        <a href={"#" + anchor} className="ec-updates-permalink" aria-label={"Link to " + entry.title}>
          Link
        </a>
      </div>
      <p className="ec-updates-summary">{entry.summary}</p>
      {displayedHighlights.length > 0 && (
        <ul className="ec-updates-highlights">
          {displayedHighlights.map((highlight, index) => (
            <li key={index}>{highlight}</li>
          ))}
        </ul>
      )}
      {expanded && entry.whyItMatters && (
        <p className="ec-updates-impact"><strong>Why it matters:</strong> {entry.whyItMatters}</p>
      )}
      <div className="ec-updates-card-bottom">
        <span className="ec-updates-kind">
          {entry.source === "published"
            ? "Announcement"
            : entry.version
              ? "Recorded version"
              : "Development milestone"}
        </span>
        {hasMore && (
          <button
            type="button"
            className="ec-updates-expand"
            onClick={onToggle}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Read full update"}
            <span aria-hidden="true">{expanded ? " ↑" : " ↓"}</span>
          </button>
        )}
      </div>
    </article>
  );
}

export default function UpdateLog() {
  const [updates, setUpdates] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [year, setYear] = useState("all");
  const [order, setOrder] = useState("newest");
  const [page, setPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    apiClient.get("/api/platform/system/updates/public", { timeout: 12000 })
      .then(({ data }) => {
        if (!active) return;
        if (!data?.success || !Array.isArray(data.updates)) {
          throw new Error("We couldn't load the update history.");
        }
        setUpdates(data.updates);
        setLatest(data.latest || data.updates[0] || null);
      })
      .catch(() => {
        if (active) setError("The update history couldn't load right now. Please try again.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [retry]);

  const categories = useMemo(() => [...new Set(updates.map((entry) => entry.category).filter(Boolean))].sort(), [updates]);
  const years = useMemo(() => [...new Set(updates.map((entry) => entry.date.slice(0, 4)))].sort().reverse(), [updates]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return updates
      .filter((entry) => category === "all" || entry.category === category)
      .filter((entry) => year === "all" || entry.date.startsWith(year))
      .filter((entry) => {
        if (!query) return true;
        return [entry.title, entry.summary, entry.category, entry.whyItMatters, ...entry.highlights]
          .some((part) => String(part || "").toLowerCase().includes(query));
      })
      // Stable sorting preserves the server's order for same-day updates.
      .sort((a, b) => order === "newest"
        ? b.date.localeCompare(a.date)
        : a.date.localeCompare(b.date));
  }, [updates, search, category, year, order]);

  useEffect(() => { setPage(1); }, [search, category, year, order]);

  // Direct footer links work even for older milestones outside the first page.
  useEffect(() => {
    const anchor = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    if (!anchor.startsWith("update-")) return;
    const index = updates.findIndex((entry) => "update-" + entry.id === anchor);
    if (index < 0) return;
    setSearch("");
    setCategory("all");
    setYear("all");
    setOrder("newest");
    setPage(Math.floor(index / PAGE_SIZE) + 1);
    setExpandedIds((current) => ({ ...current, [updates[index].id]: true }));
  }, [updates]);

  useEffect(() => {
    const anchor = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    if (!anchor.startsWith("update-") || loading || !updates.length) return;
    const element = document.getElementById(anchor);
    if (element) element.scrollIntoView({ block: "center" });
  }, [updates, loading, page]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const currentPage = Math.max(1, Math.min(page, pageCount || 1));
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const changePage = (nextPage) => {
    setPage(Math.max(1, Math.min(pageCount || 1, nextPage)));
    window.requestAnimationFrame(() => {
      document.getElementById("ec-updates-history-title")?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  };
  const hasFilters = Boolean(search.trim()) || category !== "all" || year !== "all";

  return (
    <main className="ec-updates-page">
      <div className="ec-updates-wrap">
        <nav className="ec-updates-breadcrumb" aria-label="Breadcrumb">
          <a href="/">Elite Core</a><span aria-hidden="true">/</span><span>Updates</span>
        </nav>

        <header className="ec-updates-hero">
          <div>
            <span className="ec-updates-eyebrow">ELITE CORE · PRODUCT HISTORY</span>
            <h1>Every update tells a story.</h1>
            <p>Explore how Elite Core has grown, from its first beginnings in 2025 to the tools teams use today.</p>
            <p className="ec-updates-hero-note">Historical milestones describe development progress and do not necessarily represent a public release on that date.</p>
          </div>
          {!loading && !error && updates.length > 0 && (
            <div className="ec-updates-count" aria-label={updates.length + " updates"}>
              <strong>{updates.length}</strong>
              <span>moments in the journey</span>
            </div>
          )}
        </header>

        {!loading && !error && latest && (
          <section className="ec-updates-featured" aria-label="Latest update">
            <div>
              <span className="ec-updates-featured-label">LATEST UPDATE · {prettyDate(latest.date)}</span>
              <h2>{latest.title}</h2>
              <p>{latest.summary}</p>
              <a href={"/update-log#update-" + encodeURIComponent(latest.id)}>
                Explore this update <span aria-hidden="true">↗</span>
              </a>
            </div>
            <span className="ec-updates-featured-mark" aria-hidden="true">✳</span>
          </section>
        )}

        <section className="ec-updates-content" aria-labelledby="ec-updates-history-title">
          <div className="ec-updates-section-heading">
            <div>
              <span className="ec-updates-eyebrow">FROM THE FIRST BLUEPRINT</span>
              <h2 id="ec-updates-history-title">The update log</h2>
            </div>
            {!loading && !error && <span className="ec-updates-result-count">{filtered.length} updates</span>}
          </div>

          {!loading && !error && updates.length > 0 && (
            <div className="ec-updates-controls">
              <label className="ec-updates-search">
                <span className="ec-updates-sr-only">Search updates</span>
                <span aria-hidden="true">⌕</span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search what's changed..."
                />
              </label>
              <label className="ec-updates-select">
                <span className="ec-updates-sr-only">Filter by category</span>
                <select value={category} onChange={(event) => setCategory(event.target.value)}>
                  <option value="all">All areas</option>
                  {categories.map((item) => <option value={item} key={item}>{item}</option>)}
                </select>
              </label>
              <label className="ec-updates-select">
                <span className="ec-updates-sr-only">Filter by year</span>
                <select value={year} onChange={(event) => setYear(event.target.value)}>
                  <option value="all">All years</option>
                  {years.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>
              <label className="ec-updates-select">
                <span className="ec-updates-sr-only">Sort updates</span>
                <select value={order} onChange={(event) => setOrder(event.target.value)}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </label>
            </div>
          )}

          {loading ? (
            <div className="ec-updates-state" role="status">Loading the Elite Core story...</div>
          ) : error ? (
            <div className="ec-updates-state" role="alert">
              <p>{error}</p>
              <button type="button" onClick={() => setRetry((current) => current + 1)}>Try again</button>
            </div>
          ) : !updates.length ? (
            <div className="ec-updates-state">No updates are available right now.</div>
          ) : !filtered.length ? (
            <div className="ec-updates-state">
              <p>No updates match those filters.</p>
              {hasFilters && (
                <button type="button" onClick={() => { setSearch(""); setYear("all"); setCategory("all"); }}>Clear filters</button>
              )}
            </div>
          ) : (
            <>
              <div className="ec-updates-timeline">
                {visible.map((entry, index) => {
                  const month = monthLabel(entry.date);
                  const showMonth = index === 0 || month !== monthLabel(visible[index - 1].date);
                  return (
                    <React.Fragment key={entry.id}>
                      {showMonth && <h3 className="ec-updates-month">{month}</h3>}
                      <UpdateCard
                        entry={entry}
                        expanded={Boolean(expandedIds[entry.id])}
                        onToggle={() => setExpandedIds((current) => ({ ...current, [entry.id]: !current[entry.id] }))}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
              {pageCount > 1 && (
                <nav className="ec-updates-pagination" aria-label="Update log pages">
                  <button type="button" onClick={() => changePage(currentPage - 1)}
                    disabled={currentPage === 1} aria-label="Previous page">
                    ← Previous
                  </button>
                  <span className="ec-updates-page-number" aria-live="polite">
                    Page {currentPage} of {pageCount}
                  </span>
                  <button type="button" onClick={() => changePage(currentPage + 1)}
                    disabled={currentPage === pageCount} aria-label="Next page">
                    Next →
                  </button>
                </nav>
              )}
            </>
          )}
        </section>
        <footer className="ec-updates-outro">
          <h2>Built one improvement at a time.</h2>
          <p>The story continues as Elite Core grows with the people who use it.</p>
          <a href="/">Back to Elite Core <span aria-hidden="true">→</span></a>
        </footer>
      </div>
    </main>
  );
}
